/**
 * Copyright © 2026 Adnuntius AS.
 */
import {BsDatepickerConfig, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './exploration.component.html';
import {applyNetworkCalendarWeekStart, toBsDateInputFormat} from './form-controls/bs-datepicker-locale';
import {pickerDateFromApiInstant} from '../locale/network-date-picker-utils';
import {LocaleNumberPipe} from '../filters/locale-number.pipe';
import {AppLocaleService} from '../locale/app-locale.service';
import {getIntlLocaleData} from '../locale/intl-locale-data';
import {convertIabCategoryToString} from '../../../components/util/iab-taxonomy';
import {TrafficHistogramResource} from '../../../components/api/resources/resource/traffic-histogram';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {deferMarkForCheck} from '../util/defer-mark-for-check';

interface ExplorerType {
  value: string;
  title: string;
  chartData: string;
  filterId: string;
  filterName?: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-exploration-component',
  template
})
export class ExplorationComponent implements OnInit, OnChanges {
  @Input() obj: any;
  @Input() param = 'adUnit';

  explorerTypes: ExplorerType[] = [
    {value: 'SEGMENT', title: 'Segment', chartData: 'segments', filterId: 'userSegment', filterName: true},
    {value: 'CATEGORY', title: 'Category', chartData: 'categories', filterId: 'category'},
    {value: 'KEYWORD', title: 'Keyword', chartData: 'keywords', filterId: 'keyword'},
    {value: 'KEY_VALUES', title: 'Key Value', chartData: 'keyValues', filterId: 'keyValue'},
    {value: 'COUNTRY', title: 'Country', chartData: 'countries', filterId: 'country'},
    {value: 'REGION', title: 'Region', chartData: 'regions', filterId: 'region'},
    {value: 'CITY', title: 'City', chartData: 'cities', filterId: 'city'},
    {value: 'DEVICE', title: 'Device', chartData: 'devices', filterId: 'device'},
    {value: 'CREATIVE_SIZE', title: 'Creative Size', chartData: 'creativeSizes', filterId: 'creativeSize'},
    {value: 'CREATIVE_TYPE', title: 'Creative Type', chartData: 'creativeTypes', filterId: 'creativeType'}
  ];

  startDate: moment.Moment;
  endDate: moment.Moment;
  modStartDate: Date;
  modEndDate: Date;
  showAll = false;
  hasFilter = false;
  hasUpdatedFilter = false;
  firstRunThrough = false;
  selectedTile = 'segments';
  customFilterType: string;
  customFilter: string;
  bsConfig: Partial<BsDatepickerConfig>;
  exploration: any = {
    completed: false,
    error: false
  };

  readonly boundUpdateFilter = (type: string, value: any): void => this.updateFilter(type, value);

  [key: string]: any;

  constructor(
    private readonly trafficHistogramResource: TrafficHistogramResource,
    private readonly cdr: ChangeDetectorRef,
    private readonly bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService,
    private readonly localeNumberPipe: LocaleNumberPipe,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any
  ) {
    this.localeData = getIntlLocaleData(this.appLocale.getLocaleId());
    this.bsConfig = {
      dateInputFormat: toBsDateInputFormat(this.localeData.DATETIME_FORMATS.shortDate),
      showTodayButton: true,
      showClearButton: true,
      todayPosition: 'left',
      clearPosition: 'right',
      todayButtonLabel: this.translate.instant('button.today'),
      clearButtonLabel: this.translate.instant('button.clear')
    };
  }

  ngOnInit(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );

    const networkToday = this.adnTimeShifter.getMomentInNetworkTimezone().startOf('day');
    this.startDate = networkToday.clone();
    this.endDate = networkToday.clone().add(7, 'days');
    this.modStartDate = pickerDateFromApiInstant(networkToday.toDate(), this.adnTimeShifter);
    this.modEndDate = pickerDateFromApiInstant(this.endDate.toDate(), this.adnTimeShifter);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.obj && this.obj?.id) {
      this.exploreAdUnit();
    }
  }

  clearExplorerFilter(): void {
    _.forEach(this.explorerTypes, (type) => {
      if (type.filterName) {
        _.set(this, type.filterName as any, null);
        _.set(this, (type.filterName as any) + 'AsString', null);
        _.set(this, (type.filterName as any) + 'AsStringName', null);
      }
      if (type.filterId) {
        _.set(this, type.filterId, null);
        _.set(this, type.filterId + 'AsString', null);
        _.set(this, type.filterId + 'AsStringName', null);
      }
    });
    this.hasFilter = false;
  }

  updateFilter(type: string, value: any): void {
    this.hasUpdatedFilter = true;
    _.forEach(this.explorerTypes, (t) => {
      if (t.value === type) {
        const id = _.get(value, 'id');
        const name = _.get(value, 'name');
        let filterValue;
        if (id && t.filterId) {
          this[t.filterId] = this[t.filterId] || [];
          filterValue = id;
          if (name && t.filterName) {
            filterValue = {id: filterValue, name: name};
          }
          const findMatch = _.find(this[t.filterId], (entry: any) => {
            return entry === filterValue || (!!entry.id && entry.id === filterValue.id);
          });
          if (findMatch) {
            return;
          }
          this[t.filterId].push(filterValue);
          this[t.filterId + 'AsString'] = filterValue.id ? _.map(this[t.filterId], (f: any) => {
            return f.id;
          }).join(', ') : this[t.filterId].join(', ');
          this[t.filterId + 'AsStringName'] = filterValue.name ? _.map(this[t.filterId], (f: any) => {
            return f.name;
          }).join(', ') : '';
          this.hasFilter = true;
        }
      }
    });
  }

  updateCustomFilter(): void {
    this.updateFilter(this.customFilterType, {id: this.customFilter});
    this.customFilter = null;
  }

  headingBadge(count: number): string | number {
    return count < 50 ? count : '50+';
  }

  exploreAdUnit(): void {
    if (!this.obj?.id) {
      return;
    }

    this.exploration = {
      error: false,
      completed: false,
      hasFilter: this.hasFilter
    };

    const filter: Record<string, string[]> = {};
    _.forEach(this.explorerTypes, (type) => {
      const rawValue = _.get(this, type.filterId + 'AsString', '');
      const elements = _.filter(_.map((_.isString(rawValue) ? rawValue : '').split(','), (s) => {
        return _.trim(s);
      }), (s) => {
        return _.isString(s) && s.length > 0;
      });
      const filterValue = elements.length && elements[0] !== '' ? elements : null;
      if (filterValue) {
        _.set(filter, type.value, _.map(filterValue, (item) => {
          return _.get(item, 'id') || item;
        }));
      }
    });

    const dates = {startDate: null, endDate: null};
    if (_.isDate(this.modStartDate) && _.isDate(this.modEndDate)) {
      const adjustedDates = this.adnTimeShifter.startAndEndOfDays(
        this.modStartDate,
        this.modEndDate
      );
      dates.startDate = adjustedDates.startDate;
      dates.endDate = adjustedDates.endDate;
    }

    this.trafficHistogramResource.get(
      this.obj.id,
      this.param || 'adUnit',
      _.map(this.explorerTypes, (et) => et.value),
      filter,
      dates.startDate,
      dates.endDate
    ).then((response: any) => {
      const data = response.data;
      const nextExploration: any = {
        error: false,
        completed: true,
        hasFilter: this.hasFilter
      };
      _.forEach(this.explorerTypes, (type) => {
        const rows = _.map(_.get(data, [type.value, 'histogram']) || [], (entry: any) => {
          if (type.filterName) {
            const object = _.get(entry, type.filterId);
            const id = _.get(object, 'id');
            const name = _.get(object, 'name');
            return {name: convertIabCategoryToString(name), y: entry.frequency, id: id, nextWeek: entry.expectedNextWeek};
          }
          return {name: convertIabCategoryToString(entry.value), y: entry.frequency, id: entry.value, nextWeek: entry.expectedNextWeek};
        });
        nextExploration[type.chartData] = rows;

        const chartData = _.map(rows, (row: any) => {
          return {
            name: row.name,
            y: parseFloat(this.localeNumberPipe.transform(row.y, '1.0-0'))
          };
        });
        nextExploration[type.chartData].statsChart = (!chartData || !chartData.length) ? null : {
          chart: {
            type: 'bar',
            zoomType: 'x',
            showAxes: true,
            height: Math.min(100 + chartData.length * 20, 1000)
          },
          plotOptions: {
            series: {
              colorByPoint: true,
              turboThreshold: 2000
            }
          },
          legend: {
            enabled: false
          },
          tooltip: {
            formatter: function() {
              return this.x + '<br><strong>' + this.y + '%</strong>';
            }
          },
          xAxis: {
            categories: _.map(chartData, 'name')
          },
          yAxis: {
            min: 0,
            max: 100,
            title: {
              text: 'Presence in Traffic Samples (%)'
            }
          },
          series: [{
            name: 'Presence (%)',
            data: _.map(chartData, 'y')
          }],
          title: {
            text: null
          },
          credits: {
            enabled: false
          },
          exporting: {
            enabled: true
          }
        };
      });

      let selectedTile = this.selectedTile;
      _.forEach(nextExploration, (type, key) => {
        if (_.isArray(type) && type.length > 0) {
          selectedTile = selectedTile || key;
        }
      });
      if (!selectedTile) {
        selectedTile = 'segments';
      }

      deferMarkForCheck(this.cdr, () => {
        this.firstRunThrough = true;
        this.selectedTile = selectedTile;
        this.exploration = nextExploration;
      });
    }).catch(() => {
      deferMarkForCheck(this.cdr, () => {
        this.exploration = {
          error: true,
          completed: true,
          hasFilter: this.hasFilter
        };
      });
    });
  }

}
